"use client";

// Composant client pour deux raisons, et rien d'autre : la boucle du dessin de Why Claude (les tirets qui courent le
// long des traits, les cartes qui flottent) est une animation INFINIE, donc elle doit se taire hors écran et en onglet
// caché (watchLife) ; et si l'observateur de la scène ne se déclenche pas alors que la part attendue du dessin est à
// l'écran depuis 5 s, l'entrée en scène est déclenchée ici (watchEntrance — sous cette part, le dessin attend le
// défilement, caché). Tout ce qui se voit — les sources, les traits, North, Claude — lui arrive en `children` depuis
// why.tsx, qui reste un composant serveur : rien du dessin n'est dans ce fichier, et tout se lit sans JavaScript.

import { useEffect, useRef, type ReactNode } from "react";

/* Pose data-motion sur le dessin : "live" (la boucle tourne), "idle" (elle est mise en pause LÀ où elle en est — hors
   écran, ou onglet caché), absent sous mouvement réduit — et absent TANT QUE L'OBSERVATEUR N'A PAS PARLÉ : avant sa
   première réponse on ne sait pas si le dessin est à l'écran, et un "idle" posé à l'aveugle (par un changement
   d'onglet, quand l'observateur reste muet) mettait la boucle en pause avant son premier tour. Dans why.css, la
   boucle dépend de [data-motion], et l'entrée de l'astérisque de Claude aussi (c'est le premier tour de sa boucle) ;
   le reste de l'entrée dépend du data-inview de la scène. Sans data-motion : l'image finie, immobile.
   Rend la fonction qui défait tout.
   Fonction pure, hors du composant, pour être exercée sans navigateur (tests/home-v2-why-flow.test.ts), comme
   whenGateOpen dans components/motion/reveal.tsx.
   Le patron est celui de components/home-v2/integrations.tsx, RECOPIÉ et non importé : chaque bloc garde son fichier. */
export function watchLife(flow: HTMLElement): () => void {
  const reduce = window.matchMedia("(prefers-reduced-motion: reduce)");
  let visible: boolean | undefined;

  const update = () => {
    if (reduce.matches || visible === undefined) delete flow.dataset.motion;
    else flow.dataset.motion = visible && !document.hidden ? "live" : "idle";
  };
  const io = new IntersectionObserver(
    (entries) => {
      // La DERNIÈRE entrée dit vrai : un défilement très rapide peut en livrer deux dans le même appel.
      visible = !!entries.at(-1)?.isIntersecting;
      update();
    },
    { threshold: 0 },
  );
  io.observe(flow);
  document.addEventListener("visibilitychange", update);
  reduce.addEventListener("change", update);
  return () => {
    io.disconnect();
    document.removeEventListener("visibilitychange", update);
    reduce.removeEventListener("change", update);
    delete flow.dataset.motion;
  };
}

/* Le chien de garde de l'entrée. L'entrée en scène du dessin attend le `data-inview` que components/motion/reveal.tsx
   pose sur la SCÈNE (le Reveal qui a la boîte du dessin ; le texte, au-dessus, a le sien), par un
   IntersectionObserver ; avant lui, le dessin est caché (why.css). Si cet observateur ne se déclenche jamais — ou si
   sa part, calculée une seule fois au montage, est devenue inatteignable après un redimensionnement —, le dessin
   resterait caché pour de bon. Celui-ci NE PASSE PAS par IntersectionObserver, et NE SCRUTE PAS en continu (pas de
   minuterie permanente : un lecteur qui reste en haut de page ne paie rien) :
     - il regarde, par getBoundingClientRect, si la scène est à l'écran — la MÊME part que celle que why.tsx donne à
       son Reveal (ENTRANCE_AMOUNT : 62,5 % de la boîte de la scène, plafonnés à la moitié de la fenêtre, le calcul de reveal.tsx),
       sinon il déclencherait l'entrée là où Reveal a choisi d'attendre : une fois au montage, puis à chaque `scroll`
       et `resize`, regroupés par image ;
     - quand il la voit à l'écran sans `data-inview`, onglet visible, il arme UNE minuterie de 5 s — plus long que les
       4 s que reveal.tsx laisse à l'intro du Hero, pour ne pas la court-circuiter ;
     - si elle sort de l'écran ou si l'onglet passe caché, il la désarme (le temps passé onglet caché ne compte
       jamais : au retour, les 5 s repartent de zéro) ;
     - si la minuterie arrive au bout, il pose lui-même `data-inview` ;
     - dès que `data-inview` existe — posé par la page ou par lui —, il retire tout : écouteurs, observateur
       d'attribut, minuterie. La fonction rendue fait de même au démontage.
   Il couvre CE bloc : le dessin, et le texte (à sa propre part, COPY_AMOUNT). Si le JavaScript ne s'exécute pas du
   tout, il ne tourne pas non plus — et il n'y en a pas besoin : l'état caché d'avant l'entrée n'existe que sous
   `@media (scripting: enabled)`. */
export const WATCHDOG_MS = 5000;
export const ENTRANCE_AMOUNT = 0.625;

// La part par défaut de reveal.tsx : celle du texte, que l'île garde aussi (voir watchBlock).
export const COPY_AMOUNT = 0.2;

export function watchEntrance(section: HTMLElement, amount = ENTRANCE_AMOUNT): () => void {
  let timer: ReturnType<typeof setTimeout> | undefined;
  let frame = 0;
  let done = false;

  const disarm = () => {
    if (timer !== undefined) clearTimeout(timer);
    timer = undefined;
  };
  const onScreen = () => {
    const box = section.getBoundingClientRect();
    const shown = Math.min(box.bottom, window.innerHeight) - Math.max(box.top, 0);
    // reveal.tsx : un bloc plus haut que deux fenêtres ne peut jamais montrer cette part d'un coup.
    const share = Math.min(amount, (window.innerHeight * 0.5) / Math.max(1, box.height));
    return box.height > 0 && shown >= share * box.height * 0.99;
  };
  const stop = () => {
    if (done) return;
    done = true;
    disarm();
    if (frame) window.cancelAnimationFrame(frame);
    frame = 0;
    seen.disconnect();
    window.removeEventListener("scroll", schedule);
    window.removeEventListener("resize", schedule);
    document.removeEventListener("visibilitychange", check);
  };
  const check = () => {
    // Appelé aussi par `visibilitychange`, hors image : une image peut être en attente, on l'annule avant d'oublier
    // son numéro.
    if (frame) window.cancelAnimationFrame(frame);
    frame = 0;
    if (done) return;
    if ("inview" in section.dataset) return stop();
    if (document.hidden || !onScreen()) return disarm();
    timer ??= setTimeout(() => {
      section.dataset.inview = "";
      stop();
    }, WATCHDOG_MS);
  };
  const schedule = () => {
    if (!frame && !done) frame = window.requestAnimationFrame(check);
  };
  // `data-inview` posé par la page : plus rien à garder.
  const seen = new MutationObserver(() => {
    if ("inview" in section.dataset) stop();
  });

  seen.observe(section, { attributes: true, attributeFilter: ["data-inview"] });
  window.addEventListener("scroll", schedule, { passive: true });
  window.addEventListener("resize", schedule, { passive: true });
  document.addEventListener("visibilitychange", check);
  check();
  return stop;
}

/* Les deux gardes du bloc, depuis l'île : la scène qui la porte, et le texte — le bloc a deux Reveal depuis le 12/10,
   et le texte ne doit pas rester caché non plus si son observateur se tait. Chacun à la part du sien. Le texte est
   trouvé PAR SON NOM dans la section, jamais par sa place : il est passé de droite de la scène à au-dessus d'elle, et
   un `nextElementSibling` l'avait alors perdu sans une erreur. Rend la fonction qui défait tout. */
export function watchBlock(flow: HTMLElement): () => void {
  const scene = flow.closest<HTMLElement>("[data-reveal]");
  const copy = flow.closest("section")?.querySelector<HTMLElement>(".v2-why__copy[data-reveal]");
  const stops = [scene && watchEntrance(scene), copy && watchEntrance(copy, COPY_AMOUNT)];
  return () => stops.forEach((stop) => stop?.());
}

export default function WhyFlow({ children }: { children: ReactNode }) {
  const flow = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = flow.current;
    if (!el) return;
    const stopLife = watchLife(el);
    const stopBlock = watchBlock(el);
    return () => {
      stopLife();
      stopBlock();
    };
  }, []);

  return (
    // La scène qui le porte (why.tsx) est cachée aux aides techniques : ici, seulement l'attribut data-motion.
    <div ref={flow} className="v2-why__flow">
      {children}
    </div>
  );
}
